import { api } from "../lib";
import type { FragmentCountQualifier } from "../utils/fragmentCount";

export interface StateOption {
  value: string;
  label: string;
  code: string;
  name: string;
}

export interface CityOption {
  value: string;
  label: string;
  name: string;
  stateCode: string;
  county?: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface IcdCodeOption extends SelectOption {
  code: string;
}

export interface SelectOptionWithDescription extends SelectOption {
  description?: string;
  scenarioDescription?: string;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
}

export interface InsuranceProviderOption extends SelectOption {
  name: string;
}

export interface PathologistOption {
  id: string;
  name: string;
  isDefaultQaReviewer?: boolean;
}

export interface FacilityOption {
  id: string;
  name: string;
}

export interface InsurancePlanType {
  id: string;
  type: string;
}

export interface SourceOption extends SelectOption {
  defaultCollectionMethodId?: string;
  defaultCollectionMethodName?: string;
}

// === US GEOGRAPHIC DATA ===
export const getStates = async (): Promise<StateOption[]> => {
  return api.get<StateOption[]>("/reference/states");
};

export const getCities = async (stateCode: string): Promise<CityOption[]> => {
  return api.get<CityOption[]>(`/reference/cities/${stateCode}`);
};

// === ACCOUNTS, LOCATIONS, PHYSICIANS ===
export const getAccounts = async (): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>("/reference/accounts");
};

export const getAllSurgeryCenters = async (): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>("/reference/surgery-centers");
};

export const getLabs = async (): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>("/reference/labs");
};

export const getLocationsByAccount = async (
  accountId: string
): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>(`/reference/accounts/${accountId}/locations`);
};

export const getPhysicians = async (): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>("/reference/physicians");
};

// === CLIENTS ===
export const getClients = async (): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>("/reference/clients");
};

export const checkClientExists = async (
  accountId: string,
  locationId: string | undefined,
  physicianId: string
): Promise<boolean> => {
  return api.get("/reference/clients/check", {
    params: { accountId, locationId, physicianId },
  });
};

// === SOURCES & SUBSITES ===
export const getSources = async (): Promise<SourceOption[]> => {
  return api.get<SourceOption[]>("/reference/sources");
};

export const getSubsites = async (
  sourceId: string
): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>(`/reference/sources/${sourceId}/subsites`);
};

export const getSourcesWithSubsites = async (): Promise<{
  sources: SourceOption[];
  subsitesBySource: Record<string, SelectOption[]>;
}> => {
  return api.get("/reference/sources/with-subsites");
};

// === ICD CODES ===
export const getIcdCodes = async (): Promise<IcdCodeOption[]> => {
  return api.get<IcdCodeOption[]>("/reference/icd-codes");
};

// === PROCEDURES ===
export const getProcedures = async (): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>("/reference/procedures");
};

// === INSURANCE ===
export const getInsuranceProviders = async (): Promise<
  InsuranceProviderOption[]
> => {
  return api.get<InsuranceProviderOption[]>("/reference/insurance-providers");
};

export const getInsurancePlanTypes = async (): Promise<InsurancePlanType[]> => {
  return api.get<InsurancePlanType[]>("/reference/insurance-plan-types");
};

/**
 * Get plan types allowed for a specific insurance provider
 * Used to filter plan type dropdown based on selected provider
 */
export const getPlanTypesByProvider = async (
  providerId: string
): Promise<InsurancePlanType[]> => {
  return api.get<InsurancePlanType[]>(
    `/reference/insurance-providers/${providerId}/plan-types`
  );
};

// === PATHOLOGISTS ===
export const getPathologists = async (): Promise<PathologistOption[]> => {
  return api.get<PathologistOption[]>("/reference/pathologists");
};

// === BILLING SCENARIOS ===
export const getBillingScenarios = async (): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>("/reference/billing-scenarios");
};

// === ACCESSIONING TYPES ===
export const getAccessioningTypes = async (): Promise<SelectOption[]> => {
  return api.get<SelectOption[]>("/reference/accessioning-types");
};

// === FACILITIES ===
export const getFacilities = async (): Promise<FacilityOption[]> => {
  return api.get<FacilityOption[]>("/reference/facilities");
};

export const getDefaultFacility = async (): Promise<FacilityOption | null> => {
  return api.get<FacilityOption | null>("/reference/facilities/default");
};

// === GROSS CODES ===
export const getGrossCodes = async (): Promise<
  SelectOptionWithDescription[]
> => {
  return api.get<SelectOptionWithDescription[]>("/reference/gross-codes");
};

export const getAccessioningGrossCodes = async (): Promise<
  SelectOptionWithDescription[]
> => {
  return api.get<SelectOptionWithDescription[]>(
    "/reference/gross-codes/accessioning"
  );
};

// === AI EXTRACTION MATCHING DATA ===
export interface MatchingData {
  clients: Array<{
    value: string;
    locationName: string;
    physicianName: string;
    physicianNameAlt: string;
    accountName: string;
    fullName: string;
  }>;
  procedures: Array<{
    value: string;
    name: string;
    description: string | null;
  }>;
  icdCodes: Array<{
    value: string;
    label: string;
  }>;
  insuranceProviders: Array<{
    value: string;
    name: string;
    aliases: string[];
    phoneNumbers: string[];
    addressComponents: {
      address?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  }>;
  sources: Array<{
    value: string;
    name: string;
    subsiteRequired: boolean;
  }>;
  subsites: Array<{
    value: string;
    name: string;
    sourceId: string;
  }>;
  grossCodes: Array<{
    value: string;
    name: string;
    description: string;
    isExternal: boolean;
  }>;
  physicians: Array<{
    value: string;
    lastName: string;
    firstName: string;
    middleName?: string;
    credentials?: string;
  }>;
  locations: Array<{
    locationId: string;
    locationName: string;
    accountId: string;
    accountName: string;
    externalCasePrefixes: string[];
  }>;
  providerPlanTypes: Array<{
    providerId: string;
    planTypeId: string;
    planTypeName: string;
  }>;
  // Patient address matching data
  states: Array<{
    stateCode: string;
    stateName: string;
  }>;
  cities: Array<{
    cityName: string;
    stateCode: string;
  }>;
  // Practice matching data
  practices: Array<{
    value: string;
    name: string;
  }>;
}

export const getMatchingData = async (): Promise<MatchingData> => {
  return api.get<MatchingData>("/reference/matching-data");
};

// === COLLECTION METHODS ===
export const getCollectionMethods = async (): Promise<SelectOption[]> => {
  const result = await api.get<
    Array<{ id: string; name: string; isActive: boolean }>
  >("/admin/collection-methods");
  return result
    .filter((m) => m.isActive)
    .map((m) => ({ value: m.name, label: m.name }));
};
